import { useEffect, useState, type ReactNode } from 'react';
import { Archive, FolderClosed, FolderInput, FolderPlus, FolderTree, Inbox, PencilLine, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { isFetchableDomain, type FolderCount } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { Favicon } from './Avatar';
import { Button, cn, Modal } from './ui';

/** Subfolders are paths: "Shopping/apple.com" sits inside "Shopping". */
export const folderLeaf = (path: string) => path.slice(path.lastIndexOf('/') + 1);
export const folderParent = (path: string) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : null);
export const isSelfOrInside = (path: string, folder: string) => {
  const p = path.toLowerCase();
  const f = folder.toLowerCase();
  return p === f || p.startsWith(`${f}/`);
};

/** A folder named like a domain ("apple.com", or a subfolder "Shopping/apple.com") is a filing rule and shows that site's favicon. */
export const isDomainFolder = (name: string) => isFetchableDomain(folderLeaf(name));

export function FolderIcon({ name, size = 17 }: { name: string; size?: number }) {
  if (name === 'Inbox') return <Inbox />;
  if (name === 'Archive') return <Archive />;
  return isDomainFolder(name) ? <Favicon domain={folderLeaf(name).toLowerCase()} size={size} /> : <FolderClosed />;
}

const plural = (n: number) => `${n} email${n === 1 ? '' : 's'}`;

/** A folder plus everything nested inside it: mail totals and how many subfolders. */
function subtree(folder: FolderCount, folders: FolderCount[]) {
  const all = folders.filter((f) => isSelfOrInside(f.name, folder.name));
  return { total: all.reduce((n, f) => n + f.total, 0), subfolders: all.length - 1 };
}
const subfolderNote = (n: number) => (n ? ` and its ${n} subfolder${n === 1 ? '' : 's'}` : '');

export interface MenuAction {
  icon: ReactNode;
  label: string;
  run: () => void;
  danger?: boolean;
}

/** A small menu floating at the pointer: a folder's right-click menu, or the choice after dropping one folder on another. */
export function FloatingMenu({ at, items, onClose }: { at: { x: number; y: number }; items: MenuAction[]; onClose: () => void }) {
  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const t = setTimeout(() => (window.addEventListener('click', close), window.addEventListener('contextmenu', close)), 0);
    window.addEventListener('keydown', onKey);
    window.addEventListener('blur', close);
    return () => {
      clearTimeout(t);
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', close);
    };
  }, [onClose]);
  return (
    <div
      className="glass fixed z-[60] min-w-[200px] rounded-xl bg-raised/95 p-1 shadow-2xl"
      style={{ left: Math.min(at.x, window.innerWidth - 236), top: Math.min(at.y, window.innerHeight - 8 - items.length * 32) }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((i) => (
        <button
          key={i.label}
          onClick={() => (onClose(), i.run())}
          className={cn('flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[13px] transition hover:bg-line [&_svg]:size-4', i.danger ? 'text-danger [&_svg]:text-danger' : 'text-fg [&_svg]:text-muted')}
        >
          {i.icon}
          {i.label}
        </button>
      ))}
    </div>
  );
}

/** Right-click menu for a custom folder in the sidebar. */
export function folderMenuItems(h: { onNewSubfolder: () => void; onRename: () => void; onMove: () => void; onMerge: () => void; onDelete: () => void }): MenuAction[] {
  return [
    { icon: <FolderPlus />, label: 'New subfolder…', run: h.onNewSubfolder },
    { icon: <PencilLine />, label: 'Rename…', run: h.onRename },
    { icon: <FolderTree />, label: 'Move to…', run: h.onMove },
    { icon: <FolderInput />, label: 'Merge into…', run: h.onMerge },
    { icon: <Trash2 />, label: 'Delete folder…', run: h.onDelete, danger: true },
  ];
}

/** Renames a folder in place; its subfolders and mail keep their place inside it. */
export async function renameFolder(name: string, ask: (title: string, initial: string) => Promise<string | null>): Promise<string | null> {
  const leaf = await ask(`Rename ${folderLeaf(name)}`, folderLeaf(name));
  if (!leaf?.trim() || leaf.trim() === folderLeaf(name)) return null;
  if (leaf.includes('/')) {
    toast.error('Use Move to… to put a folder inside another one.');
    return null;
  }
  const parent = folderParent(name);
  return moveFolder(name, parent ? `${parent}/${leaf.trim()}` : leaf.trim(), 'Renamed');
}

/** Changes a folder's path (rename or move), reporting the outcome. Returns the new path, or null on failure. */
export async function moveFolder(from: string, to: string, verb: 'Renamed' | 'Moved' = 'Moved'): Promise<string | null> {
  try {
    const r = await api.renameFolder(from, to);
    toast.success(`${verb} ${folderLeaf(from)}${verb === 'Moved' ? ` to ${folderParent(r.name) ?? 'the top level'}` : ` to ${folderLeaf(r.name)}`}`, {
      description: r.filed ? `Mail from ${folderLeaf(r.name)} now goes there too — moved ${plural(r.filed)} in.` : undefined,
    });
    return r.name;
  } catch (e) {
    toast.error(errorMessage(e));
    return null;
  }
}

/** Stands for "no parent" in the move picker; folder names can't contain NUL. */
const TOP = '\u0000top';

/** Picks where a folder goes: the top level or inside another folder (not itself or its own subfolders). */
export function MoveFolderDialog({ source, folders, onClose, onDone }: { source: FolderCount | null; folders: FolderCount[]; onClose: () => void; onDone: (from: string, to: string) => void }) {
  const [target, setTarget] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  useEffect(() => setTarget(undefined), [source]);
  const current = source ? folderParent(source.name) ?? TOP : undefined;
  const options = [
    { key: TOP, label: 'Top level', icon: <FolderTree /> },
    ...folders
      .filter((f) => !f.isSystem && (!source || !isSelfOrInside(f.name, source.name)))
      .map((f) => ({ key: f.name, label: f.name.split('/').join(' / '), icon: <FolderIcon name={f.name} /> })),
  ];
  const go = async () => {
    if (!source || !target) return;
    setBusy(true);
    const leaf = folderLeaf(source.name);
    const to = await moveFolder(source.name, target === TOP ? leaf : `${target}/${leaf}`);
    setBusy(false);
    if (to) onDone(source.name, to);
  };
  return (
    <Modal open={!!source} onClose={onClose} width={440}>
      {source && (
        <div className="space-y-4 p-5">
          <div className="text-[15px] font-semibold">Move {folderLeaf(source.name)} to…</div>
          <p className="text-[13px] text-muted">Its mail{subtree(source, folders).subfolders ? ' and subfolders go' : ' goes'} with it.</p>
          <div className="max-h-[300px] space-y-0.5 overflow-y-auto">
            {options.map((o) => (
              <button
                key={o.key}
                disabled={o.key === current}
                onClick={() => setTarget(o.key)}
                className={cn(
                  'flex h-9 w-full items-center gap-3 rounded-xl px-2.5 text-left text-[13.5px] transition disabled:opacity-40 [&_svg]:size-[17px]',
                  target === o.key ? 'bg-accent/15 text-fg ring-1 ring-accent/50' : 'text-muted hover:bg-line hover:text-fg',
                )}
              >
                {o.icon}
                <span className="flex-1 truncate font-medium">{o.label}</span>
                {o.key === current && <span className="text-[11.5px] text-faint">current</span>}
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={go} disabled={!target} loading={busy}>
              {target ? `Move to ${target === TOP ? 'top level' : folderLeaf(target)}` : 'Choose a place'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Moves every email in `source` into another folder and removes `source`. `initialTarget` is preselected when the
 * merge started by dropping one folder onto another.
 */
export function MergeFolderDialog({ source, folders, initialTarget, onClose, onDone }: { source: FolderCount | null; folders: FolderCount[]; initialTarget?: string; onClose: () => void; onDone: (target: string) => void }) {
  const [target, setTarget] = useState<string | undefined>(initialTarget);
  const [busy, setBusy] = useState(false);
  useEffect(() => setTarget(initialTarget), [source, initialTarget]);
  // Not into itself or one of its own subfolders: those are removed by the merge.
  const targets = folders.filter((f) => (!source || !isSelfOrInside(f.name, source.name)) && (!f.isSystem || f.name === 'Inbox' || f.name === 'Archive'));
  const tree = source ? subtree(source, folders) : { total: 0, subfolders: 0 };

  const merge = async () => {
    if (!source || !target) return;
    setBusy(true);
    try {
      const r = await api.deleteFolder(source.name, target);
      toast.success(`Merged ${source.name} into ${r.movedTo}`, { description: `Moved ${plural(r.moved)}.` });
      onDone(r.movedTo);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={!!source} onClose={onClose} width={440}>
      {source && (
        <div className="space-y-4 p-5">
          <div className="text-[15px] font-semibold">Merge {source.name} into…</div>
          <p className="text-[13px] text-muted">
            All {plural(tree.total)} in {source.name}{subfolderNote(tree.subfolders)} move to the folder you choose, then{' '}
            {tree.subfolders ? 'those folders are' : `${source.name} is`} removed.
            {isDomainFolder(source.name) && ` New mail from ${folderLeaf(source.name)} will stop being filed automatically.`}
          </p>
          <div className="max-h-[300px] space-y-0.5 overflow-y-auto">
            {targets.map((f) => (
              <button
                key={f.name}
                onClick={() => setTarget(f.name)}
                className={cn(
                  'flex h-9 w-full items-center gap-3 rounded-xl px-2.5 text-left text-[13.5px] transition [&_svg]:size-[17px]',
                  target === f.name ? 'bg-accent/15 text-fg ring-1 ring-accent/50' : 'text-muted hover:bg-line hover:text-fg',
                )}
              >
                <FolderIcon name={f.name} />
                <span className="flex-1 truncate font-medium">{f.name}</span>
                <span className="text-[11.5px] tabular-nums text-faint">{f.total}</span>
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={merge} disabled={!target} loading={busy}>
              {target ? `Merge into ${target}` : 'Choose a folder'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Removes a custom folder; its mail goes back to the Inbox or into Trash. */
export function DeleteFolderDialog({ folder, folders, onClose, onDone }: { folder: FolderCount | null; folders: FolderCount[]; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState<'Inbox' | 'Trash' | null>(null);
  const tree = folder ? subtree(folder, folders) : { total: 0, subfolders: 0 };
  const remove = async (moveTo: 'Inbox' | 'Trash') => {
    if (!folder) return;
    setBusy(moveTo);
    try {
      const r = await api.deleteFolder(folder.name, moveTo);
      toast.success(`Deleted ${folder.name}`, { description: r.moved ? `Moved ${plural(r.moved)} to ${r.movedTo}.` : undefined });
      onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal open={!!folder} onClose={onClose} width={440}>
      {folder && (
        <div className="space-y-4 p-5">
          <div className="text-[15px] font-semibold">Delete {folder.name}{subfolderNote(tree.subfolders)}?</div>
          <p className="text-[13px] text-muted">
            {tree.total
              ? `${tree.subfolders ? 'They hold' : 'It holds'} ${plural(tree.total)}. Send them back to the Inbox, or to Trash with the ${tree.subfolders ? 'folders' : 'folder'}.`
              : tree.subfolders ? 'The folders are empty.' : 'The folder is empty.'}
            {isDomainFolder(folder.name) && ` New mail from ${folderLeaf(folder.name)} will go to the Inbox again.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            {tree.total > 0 && (
              <Button variant="ghost" onClick={() => remove('Inbox')} loading={busy === 'Inbox'} disabled={!!busy}>
                Keep mail in Inbox
              </Button>
            )}
            <Button variant="danger" onClick={() => remove('Trash')} loading={busy === 'Trash'} disabled={!!busy}>
              {tree.total ? 'Delete with mail' : tree.subfolders ? 'Delete folders' : 'Delete folder'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
